// Planning page — Active Phases gantt + per-user phase / activity lists.
//
// Adapted from improvement/11749-projects-workspace's PlanningPage to use
// develop's flat resource/activity API shape and to delegate phase editing
// to the project page (clicking a row → /projects/$id) rather than the
// inline drawer that 11749 used.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Calendar, List, Activity as ActivityLucide } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { ActivePhase, MyPhase, MyActivity } from '@/types/api'

type Tab = 'gantt' | 'my-phases' | 'my-activities'

// ─── Atoms ───────────────────────────────────────────────────────────────────

function PriorityDots({ priority }: { priority: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          style={{
            width: 6, height: 6, borderRadius: '50%',
            background: i <= priority ? 'var(--ns-brand-strong)' : 'var(--ns-border-strong)',
          }}
        />
      ))}
    </span>
  )
}

// ─── Active phases gantt ─────────────────────────────────────────────────────

function ActivePhasesGantt({
  phases, onSelect,
}: {
  phases:   ActivePhase[]
  onSelect: (phase: ActivePhase) => void
}) {
  const today      = new Date()
  const year       = today.getFullYear()
  const yearStart  = new Date(year, 0, 1)
  const yearMs     = (new Date(year + 1, 0, 1).getTime() - yearStart.getTime())
  const months     = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const dayFrac = (iso: string | null, fallback: Date): number => {
    const d = iso ? new Date(iso) : fallback
    return (d.getTime() - yearStart.getTime()) / yearMs
  }
  const todayFrac = (today.getTime() - yearStart.getTime()) / yearMs

  const withDates = phases.filter(p => p.startDate || p.endDate)
  const noDates   = phases.filter(p => !p.startDate && !p.endDate)

  return (
    <div className="card" style={{ padding: 16, overflow: 'auto' }}>
      {phases.length === 0 ? (
        <div className="muted" style={{ padding: '16px 0', fontSize: 13 }}>
          No active phases yet.
        </div>
      ) : (
        <div style={{ minWidth: 720 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, marginBottom: 8 }}>
            <div />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', position: 'relative' }}>
              {months.map((m, i) => (
                <div key={i} style={{
                  fontSize: 11, color: 'var(--ns-text-muted)',
                  borderLeft: '1px solid var(--ns-border)',
                  paddingLeft: 6, paddingBottom: 4,
                }}>{m}</div>
              ))}
              {todayFrac >= 0 && todayFrac <= 1 && (
                <div style={{
                  position: 'absolute', top: 0, bottom: -8,
                  width: 1.5, background: 'var(--ns-brand-strong)',
                  left: `${todayFrac * 100}%`, zIndex: 2,
                }} />
              )}
            </div>
          </div>

          {withDates.map(p => {
            const left  = Math.max(0, dayFrac(p.startDate, yearStart) * 100)
            const right = Math.min(100, dayFrac(p.endDate, new Date(year, 11, 31)) * 100)
            const width = Math.max(right - left, 1)
            return (
              <div
                key={p.id}
                style={{
                  display: 'grid', gridTemplateColumns: '200px 1fr',
                  gap: 8, alignItems: 'center',
                  padding: '5px 0', borderTop: '1px solid var(--ns-border)',
                  cursor: 'pointer',
                }}
                onClick={() => onSelect(p)}
              >
                <div style={{ paddingRight: 8, overflow: 'hidden' }}>
                  <div style={{
                    fontSize: 12, fontWeight: 500,
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden',
                  }} title={`${p.projectName} / ${p.phaseName}`}>
                    {p.phaseName}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginTop: 1 }}>
                    {p.projectName}
                    {p.customerName && <> · {p.customerName}</>}
                  </div>
                </div>
                <div style={{ position: 'relative', height: 24, background: 'var(--ns-surface-2)', borderRadius: 4 }}>
                  <div style={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    top: 3, bottom: 3,
                    background: 'var(--ns-brand-soft)',
                    border: '1px solid var(--ns-brand-strong)',
                    borderRadius: 3,
                    display: 'flex', alignItems: 'center', paddingLeft: 6,
                    fontSize: 10.5, color: 'var(--ns-brand-strong)', fontWeight: 500,
                    whiteSpace: 'nowrap', overflow: 'hidden',
                  }}>
                    {p.resourceCount > 0 && `${p.resourceCount} resource${p.resourceCount > 1 ? 's' : ''}`}
                  </div>
                </div>
              </div>
            )
          })}

          {noDates.length > 0 && (
            <>
              <div style={{
                paddingTop: 12, paddingBottom: 4,
                fontSize: 11, color: 'var(--ns-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                borderTop: '1px solid var(--ns-border)',
              }}>
                No dates set
              </div>
              {noDates.map(p => (
                <div
                  key={p.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '200px 1fr',
                    gap: 8, alignItems: 'center',
                    padding: '5px 0', borderTop: '1px solid var(--ns-border)',
                    cursor: 'pointer',
                  }}
                  onClick={() => onSelect(p)}
                >
                  <div style={{ paddingRight: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{p.phaseName}</div>
                    <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginTop: 1 }}>{p.projectName}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>—</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── My phases ───────────────────────────────────────────────────────────────

function MyPhasesList({ phases }: { phases: MyPhase[] }) {
  if (phases.length === 0) {
    return (
      <div className="card">
        <div className="muted" style={{ padding: '16px 14px', fontSize: 13 }}>
          You are not assigned to any active phases.
        </div>
      </div>
    )
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      {phases.map((p, i) => (
        <Link
          key={p.id}
          to="/projects/$id"
          params={{ id: String(p.projectId) }}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px',
            borderBottom: i < phases.length - 1 ? '1px solid var(--ns-border)' : 'none',
            color: 'inherit', textDecoration: 'none',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 13.5 }}>{p.phaseName}</div>
            <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 3 }}>
              {p.projectName}
              {p.customerName && <> · {p.customerName}</>}
            </div>
            {(p.startDate || p.endDate) && (
              <div className="mono muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                {p.startDate ?? '—'} → {p.endDate ?? '—'}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <PriorityDots priority={p.priority} />
            {p.budgetHours != null && (
              <span className="mono muted" style={{ fontSize: 11.5 }}>{p.budgetHours}h budget</span>
            )}
          </div>
        </Link>
      ))}
    </div>
  )
}

// ─── My activities ───────────────────────────────────────────────────────────

function MyActivitiesList({ activities }: { activities: MyActivity[] }) {
  if (activities.length === 0) {
    return (
      <div className="card">
        <div className="muted" style={{ padding: '16px 14px', fontSize: 13 }}>
          You are not assigned to any activities.
        </div>
      </div>
    )
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      {activities.map((a, i) => (
        <Link
          key={a.id}
          to="/projects/$id"
          params={{ id: String(a.projectId) }}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px',
            borderBottom: i < activities.length - 1 ? '1px solid var(--ns-border)' : 'none',
            color: 'inherit', textDecoration: 'none',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 13.5 }}>{a.activityName}</div>
            <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 3 }}>
              {a.phaseName} · {a.projectName}
              {a.customerName && <> · {a.customerName}</>}
            </div>
          </div>
          <PriorityDots priority={a.priority} />
        </Link>
      ))}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PlanningPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('gantt')

  const { data: activePhases = [], isLoading: gLoading } = useQuery({
    queryKey: ['planning', 'active-phases'],
    queryFn:  () => api.get<ActivePhase[]>('/planning/active-phases'),
  })

  const { data: myPhases = [], isLoading: mpLoading } = useQuery({
    queryKey: ['planning', 'my-phases'],
    queryFn:  () => api.get<MyPhase[]>('/planning/my-phases'),
  })

  const { data: myActivities = [], isLoading: maLoading } = useQuery({
    queryKey: ['planning', 'my-activities'],
    queryFn:  () => api.get<MyActivity[]>('/planning/my-activities'),
  })

  const goToProject = (projectId: number) =>
    navigate({ to: '/projects/$id', params: { id: String(projectId) } })

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Project Planning</div>
          <h1 className="page-title">Active Phases</h1>
          <div className="page-subtitle">
            {activePhases.length} active phase{activePhases.length !== 1 ? 's' : ''}
            {myPhases.length > 0 && ` · ${myPhases.length} assigned to you`}
          </div>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 18 }}>
        <div className="stat">
          <div className="stat-label">Active phases</div>
          <div className="stat-value tabular">{gLoading ? '—' : activePhases.length}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Across all projects</div>
        </div>
        <div className="stat">
          <div className="stat-label">My phases</div>
          <div className="stat-value tabular">{mpLoading ? '—' : myPhases.length}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>You are a resource on</div>
        </div>
        <div className="stat">
          <div className="stat-label">My activities</div>
          <div className="stat-value tabular">{maLoading ? '—' : myActivities.length}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Assigned to you</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <button className={`chip${tab === 'gantt' ? ' active' : ''}`} onClick={() => setTab('gantt')} style={{ gap: 5 }}>
          <Calendar size={12} />Active Phases
        </button>
        <button className={`chip${tab === 'my-phases' ? ' active' : ''}`} onClick={() => setTab('my-phases')} style={{ gap: 5 }}>
          <List size={12} />My Phases
        </button>
        <button className={`chip${tab === 'my-activities' ? ' active' : ''}`} onClick={() => setTab('my-activities')} style={{ gap: 5 }}>
          <ActivityLucide size={12} />My Activities
        </button>
      </div>

      {tab === 'gantt' && (
        gLoading
          ? <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
          : <ActivePhasesGantt phases={activePhases} onSelect={p => goToProject(p.projectId)} />
      )}
      {tab === 'my-phases' && (
        mpLoading
          ? <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
          : <MyPhasesList phases={myPhases} />
      )}
      {tab === 'my-activities' && (
        maLoading
          ? <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
          : <MyActivitiesList activities={myActivities} />
      )}
    </div>
  )
}
